/**
 * Sales Inbox — Worklist.
 *
 * One screen for "what is left to do", grouped by company: every open sales
 * inbox email of the company's contacts mixed with the offers that need an
 * action — waiting for sales (their status carries
 * CommandLineStatus.salesActionRequired, editable on the order/offer status
 * screens) or waiting for the graphic team (the line's own actionRequiredUkr,
 * the flag the orders board already filters on).
 *
 * Design: soft cards on a tinted ground, one colour per team (violet for
 * sales, mint for graph), and a ring around each company's score so the order
 * of the board is readable before a single word is. Companies without a
 * company record keep a card of their own (and an email from an unknown sender
 * groups on its address).
 *
 * Rows go to their natural destination: an email to its inbox detail, an offer
 * to the order detail (orders/<poi>). The card itself opens the company window
 * — open work plus the full history.
 *
 * Deliberately a plain ItemView rather than a TableView: the unit of the list
 * is a company card, not a row, so the table framework's row machinery would
 * fight the layout. Paging is done on the cards by the backend, and the page
 * carries its own total — there is no second count request.
 */
define('salesInboxWorklistView',[
  'require',
  'module',
  'app',
  'marionette',
  'underscore',
  'jquery',
  'settings',
  'salesInboxWorklistRowActions',
  'salesInboxLiveUpdates',
  'entities/sales_inbox'
], function (require, Module, App, Marionette, _, $, Settings, RowActions, LiveUpdates) {
  'use strict';

  var FILTER_STORAGE_KEY = 'salesInbox.worklistFilters';
  var PAGE_SIZE = 25;
  var LIVE_INBOX = 'SALES';
  var LIVE_RELOAD_MIN_MS = 30000;
  // The score a full ring stands for. Fixed on purpose: a quiet board should
  // look quiet, not full because nothing else competes.
  var SCORE_FULL = 30;

  var SCOPES = [
    { key: 'all', label: 'All' },
    { key: 'sales', label: 'Sales' },
    { key: 'graph', label: 'Graph' }
  ];

  // Same list as the sales inbox's own locale filter, so both screens name
  // languages identically.
  var LOCALES = [
    { key: '', label: 'All languages' },
    { key: 'fr', label: 'FR' }, { key: 'nl', label: 'NL' },
    { key: 'en', label: 'EN' }, { key: 'de', label: 'DE' },
    { key: 'it', label: 'IT' }, { key: 'es', label: 'ES' },
    { key: 'da', label: 'DA' }
  ];

  // Default order is priority: the score the sales inbox gives the mails,
  // raised by VIP, waiting time and open offer value (see the badge tooltip).
  var SORTS = [
    { key: 'priority', label: 'Priority' },
    { key: 'recent', label: 'Recent' }
  ];

  // The sort a click on a sort tab asks for: "Recent" again flips its direction.
  function nextSort(clicked, current) {
    var key = clicked || 'priority';
    if (key === 'recent' && (current === 'recent' || current === 'oldest')) {
      return current === 'recent' ? 'oldest' : 'recent';
    }
    return key;
  }

  function renderSortTabs($tabs, sort) {
    $tabs.each(function () {
      var key = $(this).attr('data-sort');
      $(this).toggleClass('on', key === sort || (key === 'recent' && sort === 'oldest'));
      $(this).find('.wl-sort-dir').text(key !== 'recent' ? '' :
        (sort === 'oldest' ? ' ↑' : (sort === 'recent' ? ' ↓' : '')));
    });
  }

  // Email cases grouped by their category — the same list the sales inbox's
  // own "Email type" filter offers, so both screens name types identically.
  // Mirrors the backend EmailCase enum.
  var EMAIL_CASE_GROUPS = [
    { key: 'SALES', label: 'Sales', cases: [
      'PRICE_NEGOTIATION', 'OFFER_DECLINED', 'EXPRESS_DELIVERY', 'STOCKS',
      'PURCHASE_ORDER', 'REORDER', 'READY_TO_ORDER', 'DELIVERY_TIME', 'SAMPLE',
      'QUANTITY', 'OFFER_REQUEST', 'CHANGE', 'UNFINISHED_ORDER', 'UNDER_REVIEW'] },
    { key: 'DESIGN', label: 'Design', cases: ['ASK_FOR_MODIFICATION', 'LOGO_NOT_PRINTABLE', 'NO_LOGO_SENT'] },
    { key: 'INVOICING', label: 'Invoicing', cases: ['ASK_FOR_INVOICE', 'PAY_AFTER_DELIVERY', 'CHANGE_DATA', 'ASK_FOR_RIB', 'PAYMENT_RECEIVED'] },
    { key: 'RECLAMATION', label: 'Reclamation', cases: ['PRINTING_ISSUE', 'BROKEN_GOODS', 'MISSING_GOODS'] },
    { key: 'TRACKING', label: 'Tracking', cases: ['WHEN_WILL_ARRIVE', 'DID_NOT_ARRIVE', 'PROBLEM'] },
    { key: 'NOT_ACTIONABLE', label: 'Not actionable', cases: ['OUT_OF_OFFICE', 'EMAIL_NOT_DELIVERED', 'CARRIER_SURVEY', 'VENDOR_SOLICITATION'] }
  ];

  // A whole category is one entry of its own, so "every claim" is one click
  // instead of three. Expanded to its cases on the way to the backend, which
  // only knows cases.
  var CATEGORY_PREFIX = 'cat:';

  function typeFilterItems() {
    var items = [];
    _.each(EMAIL_CASE_GROUPS, function (g) {
      items.push({ value: CATEGORY_PREFIX + g.key, label: g.label + ' — all' });
      _.each(g.cases, function (c) {
        items.push({ value: c, label: g.label + ' · ' + prettyCase(c) });
      });
    });
    return items;
  }

  // Selection -> the emailCases the backend expects: categories expand, cases
  // pass through, and a case already covered by its category is not repeated.
  function expandTypes(selection) {
    var cases = [];
    _.each(selection || [], function (v) {
      if (v.indexOf(CATEGORY_PREFIX) !== 0) {
        cases.push(v);
        return;
      }
      var group = _.findWhere(EMAIL_CASE_GROUPS, { key: v.slice(CATEGORY_PREFIX.length) });
      if (group) { cases = cases.concat(group.cases); }
    });
    return _.uniq(cases);
  }

  // The address of a block. A company block is named by its company, a lone
  // customer by itself; a block keyed on the bare address of a sender nobody
  // knows has no file, so it has no URL either and the window leaves the slug
  // alone rather than inventing one.
  function blockUrl(customer) {
    if (!customer) { return null; }
    if (customer.companySecId) {
      return 'sales-inbox/worklist/company/' + customer.companySecId;
    }
    if (customer.customerSecId) {
      return 'sales-inbox/worklist/customer/' + customer.customerSecId;
    }
    return null;
  }

  // The "nobody is on it" choice, alongside the real user secIds.
  var UNASSIGNED = '__none__';

  function userDisplayName(u) {
    if (!u) { return ''; }
    var full = ((u.firstName || '') + ' ' + (u.lastName || '')).replace(/\s+/g, ' ');
    full = $.trim(full);
    return u.fullName || full || u.email || u.logonId || u.secId || '';
  }

  function currentUserSecId() {
    var user = Settings.get('currentUser');
    return user ? (user.secId || null) : null;
  }

  // Search scope: opened (default) | all (resolved mails too) | extended (and the
  // mail content). Filters saved by the earlier "Extended" checkbox carry searchMode.
  function openScopeOf(filters) {
    var v = filters && filters.openScope;
    if (v === 'all' || v === 'extended') { return v; }
    return filters && filters.searchMode === 'extended' ? 'extended' : 'opened';
  }

  function loadFilters() {
    try {
      var raw = window.localStorage ? window.localStorage.getItem(FILTER_STORAGE_KEY) : null;
      return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }
  }

  function saveFilters(filters) {
    try {
      if (window.localStorage) {
        window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters || {}));
      }
    } catch (e) { /* ignore quota / privacy-mode errors */ }
  }

  function initials(name) {
    if (!name) { return '?'; }
    var parts = $.trim(name).split(/\s+/);
    if (parts.length === 1) { return parts[0].slice(0, 2).toUpperCase(); }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  // The avatar gradient's second hue, kept out of CSS: see the note in the
  // stylesheet — calc() inside hsl() breaks the release minifier.
  // Avatar colour: one of ten palette classes, picked by a hash of the name so
  // a company keeps the same colour on every screen. The colours live in the
  // stylesheet — see the note there on why they cannot be computed in CSS.
  function avatarColor(name) {
    var str = String(name || '');
    var hash = 0;
    for (var i = 0; i < str.length; i++) {
      hash = (hash * 31 + str.charCodeAt(i)) % 997;
    }
    return 'c' + (hash % 10);
  }

  function formatDate(value) {
    if (!value) { return ''; }
    var d = new Date(value);
    if (isNaN(d.getTime())) { return ''; }
    return d.toLocaleString('fr-BE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }

  function ageLabel(value) {
    if (!value) { return ''; }
    var t = new Date(value).getTime();
    if (isNaN(t)) { return ''; }
    var h = (Date.now() - t) / 36e5;
    if (h < 1) { return Math.max(1, Math.round(h * 60)) + 'm'; }
    if (h < 24) { return Math.round(h) + 'h'; }
    return Math.floor(h / 24) + 'd';
  }

  // How long the card has been waiting, spelled out for the tooltip.
  function waitingLabel(oldest, days) {
    if (days > 1) { return 'waiting ' + days + ' days'; }
    if (days === 1) { return 'waiting since yesterday'; }
    var age = ageLabel(oldest);
    return age ? 'waiting ' + age : '';
  }

  // The two head pills. They answer different questions and must not be read
  // as one: "waiting" is how long the work on our plate has been pending,
  // "contact" is how long since anyone talked to this customer at all. A
  // customer can have written this morning about an offer stuck for a month.

  // Waiting turns amber after two days and red after a week — the thresholds
  // the sales team already works to.
  function waitTone(days) {
    if (days >= 7) { return 'bad'; }
    if (days >= 2) { return 'warn'; }
    return 'ok';
  }

  function waitPill(customer) {
    var since = customer.waitingSince || customer.oldestActivityDate;
    var days = customer.waitingDays || 0;
    return {
      tone: waitTone(days),
      text: ageLabel(since) || '—',
      title: since ?
        prettyCap(waitingLabel(since, days)) + ' — oldest pending item since ' + formatDate(since) :
        'Nothing pending'
    };
  }

  // A relationship with no word exchanged for a month is going cold, whoever
  // is at fault. Never having written at all is its own case.
  function contactPill(customer) {
    var when = customer.lastContactDate;
    if (!when) {
      return { tone: 'none', icon: 'fa-minus', text: 'never', title: 'No email ever exchanged with this customer' };
    }
    var days = customer.lastContactDays || 0;
    var inbound = !!customer.lastContactInbound;
    return {
      tone: days >= 30 ? 'cold' : 'ok',
      icon: inbound ? 'fa-arrow-down' : 'fa-arrow-up',
      text: ageLabel(when) || 'today',
      title: 'Last contact — ' + (inbound ? 'received from the customer' : 'sent by us') +
        ' on ' + formatDate(when)
    };
  }

  function prettyCap(s) {
    if (!s) { return ''; }
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  // The row's tooltip spells both dates out, so "3d" on a line created long
  // ago never reads as a mistake.
  function rowDateTitle(item) {
    var created = formatDate(item.date);
    if (!item.waitingSince || item.waitingSince === item.date) {
      return created;
    }
    return 'Waiting since ' + formatDate(item.waitingSince) + ' · created ' + created;
  }

  function formatAmount(value) {
    if (value === null || value === undefined || value === '') { return ''; }
    var v = Math.round(Number(value) || 0);
    return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function prettyCase(s) {
    if (!s) { return ''; }
    return String(s).replace(/_/g, ' ').toLowerCase()
      .replace(/\b([a-z])/g, function (m, c) { return c.toUpperCase(); });
  }

  // The offer's second line: order number, what it is for, how many. The
  // product is skipped when the title already says it, so nothing reads twice.
  function offerMeta(item, title) {
    return _.compact([
      item.purchaseOrderId,
      item.productName !== title ? item.productName : '',
      item.quantity ? formatAmount(item.quantity) + ' pcs' : '',
      item.statusName
    ]).join(' · ');
  }

  // The category is the bucket a case belongs to (OFFER_REQUEST lives under
  // SALES). Encoding the bucket as the colour and the case as the text puts
  // both dimensions in one tag instead of two competing ones.
  var CATEGORY_TONE = {
    SALES: 'sales',
    DESIGN: 'design',
    INVOICING: 'invoicing',
    RECLAMATION: 'claim',
    TRACKING: 'tracking',
    NOT_ACTIONABLE: 'idle'
  };

  // The classification tag: what this mail is about. Null when the classifier
  // has not run (or had nothing to say) — the row then shows no tag at all
  // rather than an empty pill.
  function classTag(item) {
    var label = prettyCase(item.emailCase || item.category);
    if (!label) { return null; }
    var full = _.compact([prettyCase(item.category), prettyCase(item.emailCase)]).join(' · ');
    return {
      tone: CATEGORY_TONE[item.category] || 'idle',
      text: label,
      title: full
    };
  }

  var STATUS_LABEL = {
    NEW: 'New',
    AWAITING: 'Awaiting',
    RESOLVED: 'Resolved',
    CLOSED: 'Closed'
  };

  // The inbox lifecycle, told by a dot — the same colour language the sales
  // inbox queue uses. It earns its place on a board of things to do: NEW is
  // untouched, AWAITING means we already answered and the ball is in the
  // customer's court, which changes what there is left to do with the row.
  function statusTag(item) {
    if (!item.inboxStatus) { return null; }
    var code = String(item.inboxStatus).toUpperCase();
    return {
      tone: 'st-' + code.toLowerCase().replace(/_/g, '-'),
      text: STATUS_LABEL[code] || prettyCase(code)
    };
  }

  // Language first: who can answer a mail is a language question before it is
  // anything else.
  function langTag(item) {
    if (!item.locale) { return null; }
    var code = String(item.locale).toUpperCase();
    var known = _.findWhere(LOCALES, { key: String(item.locale).toLowerCase() });
    return { text: code, title: known ? 'Language: ' + known.label : 'Language: ' + code };
  }

  // Which team a row waits for. A row can wait for both.
  function teamClass(item) {
    if (item.salesAction && item.graphAction) { return 'both'; }
    return item.graphAction ? 'graph' : 'sales';
  }

  function teamTitle(item) {
    if (item.salesAction && item.graphAction) { return 'Sales and graph'; }
    return item.graphAction ? 'Graph' : 'Sales';
  }

  // The ring: how full it is, and how hot. Scores are open-ended, so the ring
  // fills against a fixed reference — a quiet board keeps quiet rings instead
  // of everything looking urgent because nothing else is.
  function gaugeFill(score) {
    return Math.max(6, Math.min(100, Math.round((score || 0) / SCORE_FULL * 100)));
  }

  function gaugeHeat(score) {
    if (score >= 20) { return 'hot'; }
    if (score >= 10) { return 'warm'; }
    return 'cool';
  }

  // Reply deadline read out of the email: "14:00" today, "29/09 14:00"
  // another day; `over` once it has passed.
  function dueTag(value) {
    if (!value) { return null; }
    var d = new Date(value);
    if (isNaN(d.getTime())) { return null; }
    var time = d.toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' });
    var text = d.toDateString() === new Date().toDateString() ? time :
      d.toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit' }) + ' ' + time;
    return { text: 'reply by ' + text, over: d.getTime() <= Date.now() };
  }

  var ROW_TPL = _.template(
    '<li class="wl-row" tabindex="0" role="button" data-type="<%= type %>" data-secid="<%= secId %>" data-poi="<%= poi %>">' +
    '  <span class="wl-ic <%= teamClass %>" title="<%= teamTitle %>"><i class="fa <%= icon %>"></i></span>' +
    '  <%= dtpIcon %>' +
    '  <span class="wl-ti">' +
    '    <span class="wl-ti-t" title="<%= titleAttr %>"><%= title %></span>' +
    // The classifier's one-line summary of the mail, then the agent's note.
    '    <% if (summary) { %><span class="wl-note sw-summary" title="<%= summary %>"><i class="fa fa-magic"></i><%= summary %></span><% } %>' +
    '    <% if (note) { %><span class="wl-note" title="<%= note %>">' +
    '      <i class="fa fa-sticky-note-o"></i><%= note %></span><% } %>' +
    '  </span>' +
    '  <span class="wl-tags">' +
    // An email's status is a button: New / Awaiting / Resolved from the board.
    '    <% if (statusChip) { %><%= statusChip %><% } else if (status) { %><span class="wl-status <%= statusTone %>"><%= status %></span><% } %>' +
    '    <% if (due) { %><span class="wl-tag due<%= dueOver ? " over" : "" %>" title="Reply deadline written in the email"><%= due %></span><% } %>' +
    '    <% if (lang) { %><span class="wl-tag lang" title="<%= langTitle %>"><%= lang %></span><% } %>' +
    '    <% if (klass) { %><span class="wl-tag <%= klassTone %>" title="<%= klassTitle %>"><%= klass %></span><% } %>' +
    '  </span>' +
    '  <span class="wl-me"><%= meta %></span>' +
    '  <span class="wl-ra"><%= amount %></span>' +
    '  <% if (assignButtons) { %><%= assignButtons %><% } %>' +
    '  <span class="wl-users" data-row="<%= rowKey %>">' +
    '    <span class="wl-users-own js-users-own" title="Assigned to this item"></span>' +
    '    <span class="wl-users-cust js-users-cust" title="Assigned to the customer"></span>' +
    '  </span>' +
    '  <span class="wl-rg" title="<%= dateAttr %>"><%= age %></span>' +
    '</li>'
  );

  var BLOCK_TPL = _.template(
    '<section class="wl-card" data-key="<%= blockKey %>" data-customer="<%= customerSecId %>" data-company="<%= companySecId %>">' +
    '  <header class="wl-head" tabindex="0" role="button">' +
    '    <span class="wl-gauge <%= heat %>" style="--p:<%= fill %>" title="<%= priorityReason %>">' +
    '      <i><%= priority %></i>' +
    '    </span>' +
    '    <span class="wl-av <%= avatarColor %>"><%= avatar %></span>' +
    '    <span class="wl-who">' +
    '      <span class="wl-nm"><span class="wl-nm-t"><%= name %></span>' +
    '        <% if (vip) { %><span class="wl-vip">VIP</span><% } %></span>' +
    '      <span class="wl-sb"><%= sub %></span>' +
    '    </span>' +
    '    <span class="wl-meta">' +
    '      <span class="wl-pill wait <%= waitTone %>" title="<%= waitTitle %>">' +
    '        <i class="fa fa-hourglass-half"></i><em>waiting</em><b><%= waitText %></b></span>' +
    '      <span class="wl-pill contact <%= contactTone %>" title="<%= contactTitle %>">' +
    '        <i class="fa <%= contactIcon %>"></i><em>contact</em><b><%= contactText %></b></span>' +
    '    </span>' +
    '    <span class="wl-chips">' +
    '      <% if (salesCount) { %><span class="wl-chip sales"><%= salesCount %> sales</span><% } %>' +
    '      <% if (graphCount) { %><span class="wl-chip graph"><%= graphCount %> graph</span><% } %>' +
    '    </span>' +
    '    <span class="wl-amt"><%= value %></span>' +
    '  </header>' +
    '  <ul class="wl-rows"><%= rows %></ul>' +
    '</section>'
  );

  Module.exports = Marionette.ItemView.extend({
    className: 'sales-inbox-worklist-view',

    template: _.template(
      '<div class="wl-bar">' +
      '  <% _.each(scopes, function (s) { %>' +
      '  <button type="button" class="wl-tab js-scope <%= s.key === scope ? "on" : "" %>" data-scope="<%= s.key %>">' +
      '    <%= s.label %><span class="n"></span>' +
      '  </button>' +
      '  <% }); %>' +
      '  <span class="wl-sep"></span>' +
      '  <% _.each(sorts, function (s) { %>' +
      // "Recent" sorts both ways: a second click flips newest-first (↓) to oldest-first (↑, sort=oldest).
      '  <button type="button" class="wl-tab quiet js-sort <%= (s.key === sort || (s.key === "recent" && sort === "oldest")) ? "on" : "" %>" data-sort="<%= s.key %>"' +
      '    <% if (s.key === "recent") { %> title="Click again to flip: newest first ↓ / oldest first ↑"<% } %>>' +
      '<%= s.label %><% if (s.key === "recent") { %><span class="wl-sort-dir"><%= sort === "oldest" ? " ↑" : (sort === "recent" ? " ↓" : "") %></span><% } %></button>' +
      '  <% }); %>' +
      '  <span class="wl-lang">' +
      '    <select class="js-locale">' +
      '      <% _.each(locales, function (l) { %>' +
      '      <option value="<%= l.key %>" <%= l.key === locale ? "selected" : "" %>><%= l.label %></option>' +
      '      <% }); %>' +
      '    </select>' +
      '  </span>' +
      '  <span class="wl-who-filter js-assignees"></span>' +
      '  <span class="wl-type-filter js-types"></span>' +
      '  <label class="wl-toggle"><input type="checkbox" class="js-vip" <%= vip ? "checked" : "" %>/>VIP</label>' +
      // Only the blocks with a mail that states a reply deadline.
      '  <label class="wl-toggle" title="Only the mails that ask for a reply by a given time"><input type="checkbox" class="js-reply-by" <%= replyBy ? "checked" : "" %>/>Reply by</label>' +
      '  <span class="wl-grow"></span>' +
      // Search scope, as on the support worklist: Opened (default) / All (the
      // resolved mails matching the search too) / Extended (and the mail content, Gmail — slower).
      '  <span class="sw-open-switch" title="All: the search also finds resolved emails. Extended: and looks in the mail content too">' +
      '    <button type="button" class="wl-tab quiet js-open-scope <%= openScope === "opened" ? "on" : "" %>" data-open="opened">Opened</button>' +
      '    <button type="button" class="wl-tab quiet js-open-scope <%= openScope === "all" ? "on" : "" %>" data-open="all">All</button>' +
      '    <button type="button" class="wl-tab quiet js-open-scope <%= openScope === "extended" ? "on" : "" %>" data-open="extended" title="Also in the mail content and attachment names (slower)">Extended</button>' +
      '  </span>' +
      '  <span class="wl-find"><input type="text" class="js-search" placeholder="Company, subject, summary, order id" value="<%= search %>"/></span>' +
      '  <button type="button" class="wl-reload js-refresh" title="Refresh">⟲</button>' +
      '</div>' +
      '<div class="wl-body js-body"></div>' +
      '<div class="wl-footer js-footer"></div>'
    ),

    ui: {
      body: '.js-body',
      footer: '.js-footer',
      search: '.js-search'
    },

    events: {
      'click .js-scope': 'onScope',
      'click .js-sort': 'onSort',
      'change .js-vip': 'onVip',
      'change .js-reply-by': 'onReplyBy',
      'change .js-locale': 'onLocale',
      'click .js-refresh': 'onRefresh',
      'click .js-wl-status': 'onRowStatus',
      'click .js-wl-assign-me': 'onRowAssignMe',
      'click .js-wl-assign': 'onRowAssign',
      'keyup .js-search': 'onSearchKey',
      'click .js-open-scope': 'onOpenScope',
      'click .wl-row': 'onRowClick',
      'keydown .wl-row': 'onRowKey',
      'click .wl-head': 'onHeadClick',
      'keydown .wl-head': 'onHeadKey',
      'click .js-more': 'onMore'
    },

    initialize: function () {
      this.filters = loadFilters();
      this.page = 1;
      this.customers = [];
      this.total = 0;
      this.itemsByKey = {};
      this.userViews = [];
    },

    serializeData: function () {
      var f = this.filters;
      return {
        scopes: SCOPES,
        scope: f.scope || 'all',
        sorts: SORTS,
        locales: LOCALES,
        locale: f.locale || '',
        sort: f.sort || 'priority',
        vip: !!f.vip,
        replyBy: !!f.replyBy,
        openScope: openScopeOf(f),
        search: f.search || ''
      };
    },

    onShow: function () {
      this.renderAssigneeFilter();
      this.renderTypeFilter();
      this.fetch();
      LiveUpdates.watch(this, this.onItemsChanged);
    },

    // ---- live updates ------------------------------------------------------
    // Inbox items changed somewhere (pushed by the backend). A change to an
    // email on screen reloads the board in place — same cards loaded, same
    // scroll, no "Loading…". A change elsewhere in this inbox may add a card,
    // so it reloads too, but at most every LIVE_RELOAD_MIN_MS.
    onItemsChanged: function (items) {
      if (LiveUpdates.shownOn(this.ui.body, items).length) {
        this.reloadInPlace();
        return;
      }
      if (!LiveUpdates.concerns(items, LIVE_INBOX)) { return; }
      var wait = (this.lastLiveReload || 0) + LIVE_RELOAD_MIN_MS - Date.now();
      if (wait <= 0) {
        this.reloadInPlace();
      } else if (!this.liveReloadTimer) {
        this.liveReloadTimer = window.setTimeout(_.bind(function () {
          this.liveReloadTimer = null;
          if (!this.isDestroyed) { this.reloadInPlace(); }
        }, this), wait);
      }
    },

    scrollState: function () {
      return { win: $(window).scrollTop(), body: this.ui.body.scrollTop() };
    },

    restoreScroll: function (state) {
      this.ui.body.scrollTop(state.body);
      $(window).scrollTop(state.win);
    },

    reloadInPlace: function () {
      var self = this;
      this.lastLiveReload = Date.now();
      var params = this.params();
      params.first = 0;
      params.pageSize = Math.max(1, this.page) * PAGE_SIZE;
      App.request('salesInbox:worklist', params).done(function (page) {
        if (self.isDestroyed) { return; }
        var scroll = self.scrollState();
        self.customers = page.customers;
        self.total = page.total;
        self.renderBody();
        self.restoreScroll(scroll);
      });
    },

    /**
     * "Type": the classification the sales inbox put on a mail — a whole
     * category, or single cases inside it, any number at once. Offers carry no
     * type, so the board drops to mails only while this filter is set; the
     * empty-state copy below says so rather than leaving the reader guessing.
     */
    renderTypeFilter: function () {
      var self = this;
      var $host = this.$('.js-types');
      if (!$host.length) { return; }
      require(['multiSelectDropdownView'], function (MultiSelectDropdown) {
        if (self.isDestroyed) { return; }
        self.typeDropdown = new MultiSelectDropdown({
          items: typeFilterItems(),
          selection: self.filters.types || [],
          placeholder: 'Any type',
          countLabel: 'types',
          searchPlaceholder: 'Find a type',
          onApply: function (picked) {
            self.filters.types = (picked || []).slice();
            saveFilters(self.filters);
            self.fetch();
          }
        }).render();
        $host.append(self.typeDropdown.el);
      });
    },

    /**
     * "Assigned to": the account's users plus "unassigned", any number at once.
     * A mail matches on its own assignee; an offer matches on the users its
     * CUSTOMER is assigned to — on this board an offer belongs to whoever owns
     * the relationship.
     */
    renderAssigneeFilter: function () {
      var self = this;
      var $host = this.$('.js-assignees');
      if (!$host.length) { return; }
      require(['multiSelectDropdownView', 'entities/kanban_tickets'], function (MultiSelectDropdown) {
        if (self.isDestroyed) { return; }
        self.assigneeDropdown = new MultiSelectDropdown({
          items: [{ value: UNASSIGNED, label: 'Unassigned' }],
          selection: self.filters.assignees || [],
          placeholder: 'Anyone',
          countLabel: 'selected',
          searchPlaceholder: 'Find a user',
          onApply: function (picked) {
            self.filters.assignees = (picked || []).slice();
            saveFilters(self.filters);
            self.fetch();
          }
        }).render();
        $host.append(self.assigneeDropdown.el);

        $.when(App.request('kanban-tickets:get-users')).done(function (users) {
          if (self.isDestroyed || !self.assigneeDropdown) { return; }
          var me = currentUserSecId();
          var items = [{ value: UNASSIGNED, label: 'Unassigned' }];
          var others = [];
          _.each(users || [], function (u) {
            if (!u || !u.secId) { return; }
            // Me first: it is the pick that gets used every day.
            if (u.secId === me) {
              items.push({ value: u.secId, label: userDisplayName(u) + ' (me)' });
            } else {
              others.push({ value: u.secId, label: userDisplayName(u) });
            }
          });
          self.assigneeDropdown.setItems(items.concat(_.sortBy(others, function (o) {
            return o.label.toLowerCase();
          })));
        });
      });
    },

    // ---- query ----------------------------------------------------------
    params: function () {
      var f = this.filters;
      var params = { first: (this.page - 1) * PAGE_SIZE, pageSize: PAGE_SIZE };
      if (f.scope && f.scope !== 'all') { params.scope = f.scope; }
      if (f.sort === 'recent' || f.sort === 'oldest') { params.sort = f.sort; }
      // One filter, several choices: any number of users plus "unassigned".
      var picked = f.assignees || [];
      var users = _.without(picked, UNASSIGNED);
      if (users.length) { params.userSecIds = users; }
      if (_.contains(picked, UNASSIGNED)) { params.unassigned = true; }
      if (f.vip) { params.vip = true; }
      if (f.replyBy) { params.replyBy = true; }
      if (f.locale) { params.locale = f.locale; }
      var types = expandTypes(f.types);
      if (types.length) { params.emailCases = types; }
      if (f.search) { params.search = f.search; }
      // Resolved mails only come with a search (the server looks them up).
      var scope = openScopeOf(f);
      if (f.search && scope === 'all') { params.includeResolved = true; }
      if (f.search && scope === 'extended') { params.searchMode = 'extended'; }
      return params;
    },

    fetch: function (append) {
      var self = this;
      if (!append) {
        this.page = 1;
        this.customers = [];
        this.ui.body.html('<div class="wl-state">Loading…</div>');
      }
      App.request('salesInbox:worklist', this.params()).done(function (page) {
        if (self.isDestroyed) { return; }
        self.customers = self.customers.concat(page.customers);
        self.total = page.total;
        self.renderBody();
      }).fail(function () {
        if (self.isDestroyed) { return; }
        self.ui.body.html('<div class="wl-state wl-state-bad">' +
          'Could not load the worklist. Refresh to try again.</div>');
        self.ui.footer.empty();
      });
    },

    // ---- rendering ------------------------------------------------------
    renderBody: function () {
      if (!this.customers.length) {
        var filteringOnType = !!(this.filters.types || []).length;
        this.ui.body.html('<div class="wl-state">' +
          '<span class="wl-state-icon">' + (filteringOnType ? '🔍' : '🎉') + '</span>' +
          '<span class="wl-state-t">' + (filteringOnType ? 'No mail of that type' : 'All clear') + '</span>' +
          '<span class="wl-state-s">' + (filteringOnType ?
            'Offers carry no type, so they are left out while a type is selected.' :
            'Nothing is waiting on us right now.') + '</span></div>');
        this.ui.footer.empty();
        this.updateTabCount();
        return;
      }
      this.destroyUsers();
      var html = _.map(this.customers, this.blockHtml, this).join('');
      this.ui.body.html(html);
      this.mountUsers(this.ui.body);
      this.renderFooter();
      this.updateTabCount();
    },

    // The count belongs on the filter that produced it, not only in the footer.
    updateTabCount: function () {
      this.$('.js-scope .n').text('');
      this.$('.js-scope.on .n').text(this.total || '');
    },

    renderFooter: function () {
      var shown = this.customers.length;
      var more = shown < this.total ?
        '<button type="button" class="js-more wl-more">Show more</button>' : '';
      this.ui.footer.html(
        '<span class="wl-total"><b>' + shown + '</b> of <b>' + this.total + '</b> companies</span>' + more);
    },

    // The avatar widgets are real views, so each row keeps a key the mount step
    // can look its data up by.
    keyOf: function (item) {
      return (item.type || '') + ':' + (item.secId || '');
    },

    /**
     * Mount the shared avatars component on every row of `$scope`: one widget
     * for the users assigned to the row, one for the users assigned to its
     * customer. Same component as the orders board — read-only here, the board
     * is where assignments are made.
     */
    mountUsers: function ($scope) {
      var self = this;
      var $rows = $scope.find('.wl-users');
      if (!$rows.length) { return; }
      require(['usersView', 'backbone'], function (UsersView, Backbone) {
        if (self.isDestroyed) { return; }
        $rows.each(function () {
          var $host = $(this);
          var item = self.itemsByKey[$host.attr('data-row')];
          if (!item) { return; }
          var users = item.userpids || [];
          self.mountUserGroup($host.find('.js-users-own'), UsersView, Backbone,
            _.where(users, { customerLinked: false }));
          self.mountUserGroup($host.find('.js-users-cust'), UsersView, Backbone,
            _.where(users, { customerLinked: true }));
        });
      });
    },

    mountUserGroup: function ($host, UsersView, Backbone, users) {
      if (!$host.length || !users.length) { return; }
      var view = new UsersView({
        model: new Backbone.Model({ userpids: users }),
        readOnly: true
      });
      view.render();
      $host.append(view.el);
      // UsersView builds its collection in onShow, which nothing triggers for a
      // view mounted by hand.
      view.triggerMethod('show');
      this.userViews.push(view);
    },

    destroyUsers: function () {
      _.each(this.userViews, function (view) { view.destroy(); });
      this.userViews = [];
    },

    // Index the blocks by their DOM key so a click can hand the popup the very
    // model the row was rendered from (no refetch, no lookup by name).
    blockKey: function (customer) {
      return customer.companySecId || customer.customerSecId ||
        customer.customerEndUserId || customer.customerEmail || '';
    },

    blockHtml: function (customer) {
      _.each(customer.items || [], function (item) {
        this.itemsByKey[this.keyOf(item)] = item;
      }, this);
      var rows = _.map(customer.items || [], this.rowHtml, this).join('');
      var title = customer.companyName || customer.customerName ||
        customer.customerEmail || 'Unknown customer';
      var contacts = _.compact(_.map(customer.contacts || [], function (c) {
        return c.name || c.email || c.endUserId;
      }));
      // Two names, then a count — a wall of contacts helps nobody.
      var shown = contacts.slice(0, 2);
      if (contacts.length > shown.length) { shown.push('+' + (contacts.length - shown.length)); }
      // The waiting time left the sub-line: it now has a pill of its own, and
      // repeating it here only pushed the contact names out of sight.
      var sub = shown.length ? shown.join(' · ') : (customer.customerEmail || '');
      var wait = waitPill(customer);
      var contact = contactPill(customer);

      return BLOCK_TPL({
        blockKey: _.escape(this.blockKey(customer)),
        customerSecId: _.escape(customer.customerSecId || ''),
        companySecId: _.escape(customer.companySecId || ''),
        avatar: _.escape(initials(title)),
        avatarColor: avatarColor(title),
        name: _.escape(title),
        vip: !!customer.customerVip,
        sub: _.escape(sub),
        waitTone: wait.tone,
        waitText: _.escape(wait.text),
        waitTitle: _.escape(wait.title),
        contactTone: contact.tone,
        contactIcon: contact.icon,
        contactText: _.escape(contact.text),
        contactTitle: _.escape(contact.title),
        salesCount: customer.salesCount || 0,
        graphCount: customer.graphCount || 0,
        value: customer.offerValue ? formatAmount(customer.offerValue) + ' €' : '',
        priority: customer.priorityScore || 0,
        fill: gaugeFill(customer.priorityScore),
        heat: gaugeHeat(customer.priorityScore || 0),
        priorityReason: _.escape(customer.priorityReason || 'No boost'),
        rows: rows
      });
    },

    rowHtml: function (item) {
      var isOffer = item.type === 'OFFER';
      // An offer is named by its own label when it has one, by the product
      // otherwise — a bare order number tells the reader nothing.
      var title = isOffer ?
        (item.title || item.productName || item.purchaseOrderId || '') :
        (item.title || '(no subject)');
      // The classification left the grey meta line — it has a tag of its own
      // now, and repeating it there only crowded out the sender.
      var meta = isOffer ?
        offerMeta(item, title) :
        (item.senderEmail || '');
      var lang = isOffer ? null : langTag(item);
      var klass = isOffer ? null : classTag(item);
      var status = isOffer ? null : statusTag(item);
      var due = isOffer ? null : dueTag(item.replyDueAt);
      return ROW_TPL({
        due: due ? _.escape(due.text) : '',
        dueOver: !!(due && due.over),
        type: isOffer ? 'OFFER' : 'EMAIL',
        icon: isOffer ? 'fa-file-text-o' : 'fa-envelope-o',
        teamClass: teamClass(item),
        teamTitle: teamTitle(item),
        dtpIcon: RowActions.dtpIcon(item),
        secId: _.escape(item.secId || ''),
        poi: _.escape(item.purchaseOrderId || ''),
        title: _.escape(title),
        titleAttr: _.escape(title),
        note: _.escape(item.note || ''),
        summary: _.escape(item.summary || ''),
        meta: _.escape(meta),
        status: status ? _.escape(status.text) : '',
        statusChip: isOffer ? '' : RowActions.statusChip(item),
        assignButtons: isOffer ? '' : RowActions.assignButtons(item),
        statusTone: status ? status.tone : '',
        lang: lang ? _.escape(lang.text) : '',
        langTitle: lang ? _.escape(lang.title) : '',
        klass: klass ? _.escape(klass.text) : '',
        klassTone: klass ? klass.tone : '',
        klassTitle: klass ? _.escape(klass.title) : '',
        amount: isOffer && item.price ? formatAmount(item.price) + ' €' : '',
        rowKey: this.keyOf(item),
        // The wait, not the creation date: an offer drawn up months ago and
        // only just put back on us has been waiting since that move. The
        // creation date stays one hover away.
        age: ageLabel(item.waitingSince || item.date),
        dateAttr: _.escape(rowDateTitle(item))
      });
    },

    // ---- interactions ---------------------------------------------------
    onScope: function (e) {
      this.filters.scope = $(e.currentTarget).data('scope');
      saveFilters(this.filters);
      this.$('.js-scope').removeClass('on');
      $(e.currentTarget).addClass('on');
      this.fetch();
    },

    onSort: function (e) {
      this.filters.sort = nextSort($(e.currentTarget).attr('data-sort'), this.filters.sort);
      saveFilters(this.filters);
      renderSortTabs(this.$('.js-sort'), this.filters.sort);
      this.fetch();
    },

    // Keeps only the work handled in that language: the mails the inbox
    // detected as such, and the offers whose customer shops in it.
    onLocale: function (e) {
      this.filters.locale = $(e.currentTarget).val() || null;
      saveFilters(this.filters);
      this.fetch();
    },

    onVip: function (e) {
      this.filters.vip = e.currentTarget.checked ? true : null;
      saveFilters(this.filters);
      this.fetch();
    },

    onOpenScope: function (e) {
      this.filters.openScope = $(e.currentTarget).attr('data-open');
      delete this.filters.searchMode;
      this.$('.js-open-scope').removeClass('on');
      $(e.currentTarget).addClass('on');
      saveFilters(this.filters);
      // Without a search every scope shows the same board: no need to refetch.
      if (this.filters.search) { this.fetch(); }
    },

    onReplyBy: function (e) {
      this.filters.replyBy = e.currentTarget.checked ? true : null;
      saveFilters(this.filters);
      this.fetch();
    },

    onRefresh: function () {
      this.fetch();
    },

    // ---- row actions (status / assignee of an email) ---------------------
    // The buttons sit inside the row, whose click opens the email: keep the
    // click to themselves. The board is refetched once the change is saved.
    onRowStatus: function (e) {
      e.preventDefault();
      e.stopPropagation();
      RowActions.openStatusMenu($(e.currentTarget), _.bind(this.afterRowAction, this));
    },

    onRowAssignMe: function (e) {
      e.preventDefault();
      e.stopPropagation();
      $(e.currentTarget).prop('disabled', true);
      RowActions.assignMe($(e.currentTarget).attr('data-secid'), _.bind(this.afterRowAction, this));
    },

    onRowAssign: function (e) {
      e.preventDefault();
      e.stopPropagation();
      RowActions.openAssignee($(e.currentTarget), _.bind(this.afterRowAction, this));
    },

    afterRowAction: function () {
      if (this.isDestroyed) { return; }
      this.fetch();
    },

    onSearchKey: function (e) {
      var self = this;
      var value = $.trim($(e.currentTarget).val());
      if (e.keyCode === 13) {
        this.applySearch(value);
        return;
      }
      // Debounce the free-text search so typing doesn't fire a request per key.
      if (this.searchTimer) { window.clearTimeout(this.searchTimer); }
      this.searchTimer = window.setTimeout(function () {
        self.applySearch(value);
      }, 400);
    },

    applySearch: function (value) {
      if ((this.filters.search || '') === value) { return; }
      this.filters.search = value || null;
      saveFilters(this.filters);
      this.fetch();
    },

    onMore: function () {
      this.page += 1;
      this.fetchMore();
    },

    // Append the next page of customer blocks without clearing the ones on
    // screen (the count is already known, so only the list call is needed).
    fetchMore: function () {
      var self = this;
      App.request('salesInbox:worklist', this.params()).done(function (page) {
        if (self.isDestroyed) { return; }
        self.customers = self.customers.concat(page.customers);
        self.total = page.total;
        var $added = $(_.map(page.customers, self.blockHtml, self).join(''));
        self.ui.body.append($added);
        self.mountUsers($added);
        self.renderFooter();
      });
    },

    // Enter and Space act like a click, so the board is usable without a mouse.
    onRowKey: function (e) {
      if (e.keyCode !== 13 && e.keyCode !== 32) { return; }
      e.preventDefault();
      this.onRowClick(e);
    },

    onHeadKey: function (e) {
      if (e.keyCode !== 13 && e.keyCode !== 32) { return; }
      e.preventDefault();
      this.onHeadClick(e);
    },

    onRowClick: function (e) {
      var $row = $(e.currentTarget);
      // attr() rather than data(): jQuery coerces data values, which would eat
      // the leading zeros of a numeric purchase order id.
      if ($row.attr('data-type') === 'OFFER') {
        var poi = $row.attr('data-poi');
        if (poi) {
          require(['ordersDetailView'], function (DetailView) {
            DetailView.prototype.open(poi);
          });
        }
        return;
      }
      // An email opens in its own window too: the board is a place you come
      // back to, so nothing here navigates away from it.
      var secId = $row.attr('data-secid');
      if (!secId) { return; }
      var title = $row.find('.wl-ti').text();
      require(['salesInboxDetailView'], function (DetailView) {
        DetailView.prototype.open(secId, title);
      });
    },

    // The block header opens the recap popup: the open work of that company
    // plus its timeline. The company/customer file stays one click away from
    // inside the popup.
    onHeadClick: function (e) {
      var key = $(e.currentTarget).closest('.wl-card').attr('data-key');
      var customer = _.find(this.customers, function (c) {
        return this.blockKey(c) === key;
      }, this);
      if (!customer) { return; }
      // Same window component as the offer/order detail, so the recap can be
      // moved, resized and left open next to the list.
      require(['windows', 'salesInboxWorklistPopupView'], function (Windows, PopupView) {
        Windows.open({
          view: function () { return new PopupView({ customer: customer }); },
          // Same contract as the item window and the order detail: while this
          // window has the focus the slug names what it shows, so the browser's
          // back button leads back to the board.
          navigation: function () {
            var defer = $.Deferred();
            var url = blockUrl(customer);
            // No file, no URL: resolving nothing leaves the slug untouched.
            if (url) { defer.resolve(url); }
            return defer.promise();
          },
          id: 'worklist-customer:' + key,
          className: 'worklist-customer-window',
          title: customer.companyName || customer.customerName ||
            customer.customerEmail || 'Worklist',
          width: '86%',
          height: '90%'
        });
      });
    },

    onDestroy: function () {
      if (this.searchTimer) { window.clearTimeout(this.searchTimer); }
      if (this.liveReloadTimer) { window.clearTimeout(this.liveReloadTimer); }
      this.destroyUsers();
      if (this.assigneeDropdown) {
        this.assigneeDropdown.destroy();
        this.assigneeDropdown = null;
      }
    }
  });
});

